/**
 * Bank Service
 * 
 * Service สำหรับจัดการ Web Bank Account
 */

import type { WebBankAccount, WebBankAccountQueryParams } from './bank.types'
import { queryClass } from '@/services/adapters/parse/parse-client'

// ============================================
// Mapping: Parse -> UI
// ============================================

function parseWebBankToUI(data: any): WebBankAccount {
  // Angular เก็บ type เป็น "Deposit", "Withdraw", "Both"
  let bankType: 'deposit' | 'withdraw' = 'deposit'
  if (data.type === 'Withdraw') {
    bankType = 'withdraw'
  } else if (data.type === 'Both') {
    // ถ้าเป็น Both ให้ใช้ได้ทั้ง deposit และ withdraw
    // แต่ต้อง return ค่าเดียว ดังนั้นใช้ primaryWithdraw เป็นตัวตัดสิน
    bankType = data.primaryWithdraw ? 'withdraw' : 'deposit'
  }
  
  return {
    id: data.objectId,
    name: data.name || '',
    bankName: data.bank_name || '',
    bankAccount: data.bank_account || '',
    imageUrl: data.image_url || '',
    amount: parseFloat(data.amount) || 0,
    status: data.status === 'Active' ? 'Active' : 'Inactive',
    type: bankType,
    isBoth: data.type === 'Both', // เพิ่ม flag สำหรับ Both
    primaryDeposit: data.primaryDeposit || false,
    primaryWithdraw: data.primaryWithdraw || false,
    gatewayApi: data.gatewayApi || false, // ✅ Gateway API capability
    bankTag: data.bank_tag,
    seq: data.seq,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  }
}

// ============================================
// Bank Service
// ============================================

export const BankService = {
  /**
   * ดึงรายการ Web Bank Account ทั้งหมด (Active only)
   */
  async getWebBankAccounts(
    _sessionToken: string,
    params: WebBankAccountQueryParams = {}
  ): Promise<WebBankAccount[]> {
    console.log('🏦 [BankService] getWebBankAccounts:', params)
    
    try {
      const where: Record<string, any> = {
        status: 'Active',
        deletedAt: { $exists: false },
      }
      
      // ไม่ filter ด้วย type เพราะอาจไม่มี field นี้ในบาง records
      // if (params.type) {
      //   where.type = params.type
      // }
      
      const result = await queryClass('WebBankAccount', {
        where,
        limit: params.limit || 100,
        skip: params.page ? (params.page - 1) * (params.limit || 100) : 0,
        order: 'seq',
      })
      
      console.log('🏦 [BankService] Result:', {
        count: result.count,
        resultsCount: result.results?.length,
        firstBank: result.results?.[0],
      })
      
      return (result.results || []).map(parseWebBankToUI)
    } catch (error) {
      console.error('❌ [BankService] Error:', error)
      throw error
    }
  },
  
  /**
   * ดึง Web Bank Account เดียว
   */
  async getWebBankAccount(
    _sessionToken: string,
    id: string
  ): Promise<WebBankAccount | null> {
    console.log('🏦 [BankService] getWebBankAccount:', id)
    
    try {
      const result = await queryClass('WebBankAccount', {
        where: { objectId: id },
        limit: 1,
      })
      
      if (!result.results || result.results.length === 0) {
        return null
      }
      
      return parseWebBankToUI(result.results[0])
    } catch (error) {
      console.error('❌ [BankService] Error:', error)
      throw error
    }
  },
  
  /**
   * ดึงบัญชีหลักสำหรับฝาก
   */
  async getPrimaryDepositBank(sessionToken: string): Promise<WebBankAccount | null> {
    const banks = await this.getWebBankAccounts(sessionToken, { type: 'deposit' })
    return banks.find(b => b.primaryDeposit) || banks[0] || null
  },
  
  /**
   * ดึงบัญชีหลักสำหรับถอน
   */
  async getPrimaryWithdrawBank(sessionToken: string): Promise<WebBankAccount | null> {
    const banks = await this.getWebBankAccounts(sessionToken, { type: 'withdraw' })
    return banks.find(b => b.primaryWithdraw) || banks[0] || null
  },
}

export default BankService
